import {
    ActionService,
    ApprovalService,
    AssistantService,
    AuthService,
    BootstrapService,
    browserStore,
    ChatterService,
    CommandBus,
    CommandEngineRegistry,
    createPendingCommandRegistry,
    createPendingTrackMiddleware,
    EventBus,
    HttpClient,
    InsightService,
    LocaleService,
    NotificationService,
    OmniSearchService,
    RecordService,
    registerCoreEngines,
    ReportService,
    RequestRegistry,
    SessionStore,
    SettingsService,
    SseClient,
    WorkflowService,
} from "../../core";
import type { Json, KeyValueStore, PendingCommandRegistry, ServiceRegistry } from "../../core";
import { toast } from "../hooks/use-toast";

// Pull a human-readable message out of whatever the engine / handler
// threw. The backend tends to surface failures as a stringified HTTP
// envelope like:
//   `422: {"error": "handler_validation_failed", "message": "..."}`
// (and occasionally as a Python repr with single quotes when the body
// isn't JSON-encoded). Toasting that raw string drops a wall of curly
// braces on the user; instead we strip the status prefix, try to
// JSON-parse, and unwrap the inner `message` field. Falls through to
// the raw text for non-envelope errors so we never SWALLOW information.
function errorMessage(error: unknown): string {
    const raw = error instanceof Error ? error.message : String(error);
    // Strip leading `<NNN>: ` HTTP-status prefix.
    const stripped = raw.replace(/^\s*\d{3}\s*:\s*/, "");
    // JSON envelope — the common shape `{"message": "..."}`.
    try {
        const obj = JSON.parse(stripped) as Record<string, unknown>;
        if (obj && typeof obj.message === "string" && obj.message.trim()) {
            return obj.message;
        }
        if (obj && typeof obj.detail === "string" && obj.detail.trim()) {
            return obj.detail;
        }
    } catch {
        // not JSON — fall through to regex
    }
    // Python-repr envelope: single-quoted keys/values that JSON.parse
    // can't read. Regex-extract the `message` value directly.
    const m = stripped.match(/['"]message['"]\s*:\s*['"]([^'"]+(?:\\.[^'"]+)*)['"]/);
    if (m && m[1]) return m[1];
    return raw;
}

// The composition root. Pure TypeScript (no React) — it wires the core pieces into
// a single Runtime. Swapping the view framework reuses this untouched.
export interface Runtime {
    commandBus: CommandBus;
    eventBus: EventBus;
    sse: SseClient;
    requests: RequestRegistry;
    services: ServiceRegistry;
    session: SessionStore;
    /** Raw HTTP client — shared with every service in `services`.
        Exposed so ad-hoc consumers (admin debug pages, custom client
        actions) can hit endpoints that don't yet have a typed service.
        New work should prefer adding a typed service over reaching for
        this slot; it's here for prototypes + diagnostics. */
    http: HttpClient;
    // Button-dispatch engine registry — RUN_BUTTON handler resolves the engine
    // for each dispatch from here. Bootstrap pre-registers core engines
    // (workflow / approval / report / default); domains can register more.
    commandEngines: CommandEngineRegistry;
    // Per-signature pending tracker — exposed so React hooks can subscribe.
    // The pendingTrack middleware on the bus writes here.
    pendingCommands: PendingCommandRegistry;
}

export interface BuildRuntimeOptions {
    baseUrl?: string;
    store?: KeyValueStore;
    fetchFn?: typeof fetch;
    onUnauthorized?: () => void;
    ssePath?: string;
}

export function buildRuntime(opts: BuildRuntimeOptions = {}): Runtime {
    const baseUrl = opts.baseUrl ?? "";
    const session = new SessionStore(opts.store ?? browserStore());
    const eventBus = new EventBus();
    const emit = (name: string, payload: Json) => eventBus.emit(name, payload);

    // Bare client for auth — NO refresh wiring, so /api/auth/refresh can't loop.
    const authHttp = new HttpClient({
        baseUrl,
        getAccessToken: () => session.getAccessToken(),
        fetchFn: opts.fetchFn,
    });
    const auth = new AuthService(authHttp, session);

    // Main client — refreshes via AuthService, surfaces 403/500 as events.
    const http = new HttpClient({
        baseUrl,
        getAccessToken: () => session.getAccessToken(),
        refreshAccessToken: () => auth.refresh(),
        onUnauthorized: opts.onUnauthorized,
        emit,
        fetchFn: opts.fetchFn,
    });

    const services: ServiceRegistry = {
        auth,
        bootstrap: new BootstrapService(http),
        action: new ActionService(http),
        record: new RecordService(http),
        locale: new LocaleService(http),
        report: new ReportService(http),
        omniSearch: new OmniSearchService(http),
        approval: new ApprovalService(http),
        notification: new NotificationService(http),
        workflow: new WorkflowService(http),
        insights: new InsightService(http),
        chatter: new ChatterService(http),
        assistant: new AssistantService(http, session, baseUrl, opts.fetchFn),
        settings: new SettingsService(http),
    };

    const sse = new SseClient({
        baseUrl,
        getAccessToken: () => session.getAccessToken(),
        fetchFn: opts.fetchFn,
    });
    // The central inbound bridge: every server push fans out top→down via the EventBus.
    sse.onMessage((message) => eventBus.emit(message.name, message.payload));

    const commandBus = new CommandBus();
    const pendingCommands = createPendingCommandRegistry();
    // Pending-track BEFORE error-toast so the spinner is cleared (in the
    // `finally`) before the destructive toast renders. Coalesces double-clicks
    // at the bus level — second dispatch with the same signature is dropped
    // while the first is in flight.
    commandBus.use(createPendingTrackMiddleware(pendingCommands));
    // Any command that throws surfaces a destructive toast, then re-throws so
    // callers can still handle it. (Radix Toast.)
    commandBus.use(async (_command, next) => {
        try {
            return await next();
        } catch (error) {
            // Destructive (B1) — red strip + alert icon + description so the
            // user sees what went wrong, not just "something failed". The
            // error message comes from whatever the engine / handler threw.
            toast({
                variant: "destructive",
                title: "Action failed",
                description: errorMessage(error),
            });
            throw error;
        }
    });

    const commandEngines = new CommandEngineRegistry();
    registerCoreEngines(commandEngines);

    return {
        commandBus,
        eventBus,
        sse,
        requests: new RequestRegistry(),
        services,
        session,
        http,
        commandEngines,
        pendingCommands,
    };
}
